// How a data element's submittals are filled in, which decides what its payload
// holds and so how the detail page can render it. Absent means eform — the mode
// was introduced after the eform data elements.
export const SUBMISSION_MODE = {
  EFORM: 'eform',
  EXCEL_UPLOAD: 'excel-upload',
}

export const SUBMITTAL_STATUS = {
  DRAFT: 'DRAFT',
  PENDING_REVIEW: 'PENDING_REVIEW',
  REJECTED: 'REJECTED',
  REPLACED: 'REPLACED',
  APPROVED: 'APPROVED',
  DELETED: 'DELETED',
  PASSED_DEADLINE: 'PASSED_DEADLINE',
  // Reachable only on a data element that requires a second approval — the
  // precast (two-stage) flow. The single-stage maps below deliberately omit
  // them: their presentation depends on which org approves, which only the data
  // element's config knows, so it is resolved by resolveStatusTag instead.
  SECONDARY_APPROVED: 'SECONDARY_APPROVED',
  SECONDARY_REJECTED: 'SECONDARY_REJECTED',
}

export const SUBMITTAL_STATUS_LABEL = {
  [SUBMITTAL_STATUS.DRAFT]: 'Draft',
  [SUBMITTAL_STATUS.PENDING_REVIEW]: 'Pending Review',
  [SUBMITTAL_STATUS.REJECTED]: 'Rejected',
  [SUBMITTAL_STATUS.REPLACED]: 'Replaced',
  [SUBMITTAL_STATUS.APPROVED]: 'Approved',
  [SUBMITTAL_STATUS.DELETED]: 'Deleted',
  [SUBMITTAL_STATUS.PASSED_DEADLINE]: 'Passed Deadline',
}

export const SUBMITTAL_STATUS_PILL_MODIFIER = {
  [SUBMITTAL_STATUS.DRAFT]: 'draft',
  [SUBMITTAL_STATUS.PENDING_REVIEW]: 'pending-review',
  [SUBMITTAL_STATUS.REJECTED]: 'rejected',
  [SUBMITTAL_STATUS.REPLACED]: 'replaced',
  [SUBMITTAL_STATUS.APPROVED]: 'approved',
  [SUBMITTAL_STATUS.DELETED]: 'deleted',
  [SUBMITTAL_STATUS.PASSED_DEADLINE]: 'passed-deadline',
}

// The first reviewer in a two-stage workflow is always the consultant, so
// unlike the second approver — which varies per data element and comes from
// `secondaryApprovalLabel` — this one is fixed.
export const FIRST_APPROVER = 'Consultant'

// Stands in for a two-stage element that never named its second approver, so
// copy naming {org} stays grammatical ("approved by Approver") rather than
// trailing off after "by" or "to". Matches the service's own fallback for the
// same situation in its emails.
export const DEFAULT_SECONDARY_LABEL = 'Approver'

/**
 * How each status is presented, ported from pitstop-ui so a submittal reads the
 * same in both apps. `variant` is a role name, not a colour: the mapping to
 * Tailwind classes lives in SubmittalStatusTag.
 */
export const SUBMITTAL_STATUS_TAGS = {
  DRAFT: { label: 'Draft', variant: 'neutral', icon: 'history' },
  REPLACED: { label: 'Replaced', variant: 'neutral', icon: 'x-circle' },
  DELETED: { label: 'Deleted', variant: 'neutral', icon: 'clock' },
  PASSED_DEADLINE: { label: 'Passed Deadline', variant: 'critical', icon: 'clock' },

  // First-stage states. `whenSecondaryRequired` replaces the presentation when
  // the data element has a second approver, where the one-stage wording would
  // mislead. It may override the styling too, not just the label — see APPROVED.
  PENDING_REVIEW: {
    label: 'Pending Review',
    variant: 'warning',
    icon: 'history',
    whenSecondaryRequired: { label: `Pending ${FIRST_APPROVER} Review` },
  },
  APPROVED: {
    label: 'Approved',
    variant: 'success',
    icon: 'check-double',
    // On a two-stage element the consultant's approval is not the end of the
    // road — the submittal is now waiting on the second approver. A green tick
    // would be misleading for this being a pending state, contradicting itself.
    whenSecondaryRequired: { label: 'Pending {org} Review', variant: 'warning', icon: 'history' },
  },
  REJECTED: {
    label: 'Rejected',
    variant: 'critical',
    icon: 'x-circle',
    whenSecondaryRequired: { label: `Rejected by ${FIRST_APPROVER}` },
  },

  // Second-stage states, named by the second approver. Never keyed off
  // `required` — reaching one of these already implies a second approval.
  SECONDARY_APPROVED: {
    label: 'Approved',
    labelWithOrg: 'Approved by {org}',
    variant: 'success',
    icon: 'check-double',
    secondStage: true,
  },
  SECONDARY_REJECTED: {
    label: 'Rejected',
    labelWithOrg: 'Rejected by {org}',
    variant: 'critical',
    icon: 'x-circle',
    secondStage: true,
  },
}

// Substitutes the second approver's name. An element can require a second
// approval without naming one, so an absent org collapses the placeholder rather
// than leaving "Pending  Review" or a literal "{org}" on screen.
export const withOrg = (text, org) =>
  text
    .replace('{org}', org || '')
    .replace(/\s{2,}/g, ' ')
    .trim()

/**
 * Resolves the presentation of a status for a given element's workflow: the
 * label plus the styling that must agree with it.
 *
 * `secondaryApproval.label` names the second approver; `.required` says whether
 * the element has one at all. They are separate because an element can require a
 * second approval without naming it.
 *
 * Returns null for an unrecognised status so callers can surface the raw code
 * rather than render a blank tag.
 */
export const resolveStatusTag = (status, { required, label } = {}) => {
  const tag = SUBMITTAL_STATUS_TAGS[status]
  if (!tag) return null

  // Second-stage states name the org — reaching one already implies a second
  // approval — but only when there is a name to use: "Rejected by {org}" with
  // no org would read "Rejected by". First-stage states differ only when a
  // second approval is required; their wording puts the placeholder mid-phrase,
  // so it collapses cleanly to "Pending Review" when unnamed.
  const override =
    tag.labelWithOrg && label
      ? { label: tag.labelWithOrg }
      : (required && tag.whenSecondaryRequired) || null

  const resolved = { ...tag, ...override }
  return {
    label: withOrg(resolved.label, label),
    variant: resolved.variant,
    icon: resolved.icon,
  }
}

/**
 * Display text for a status code, or the code itself when unrecognised so a gap
 * is visible rather than blank.
 */
export const statusLabel = (status, options) => resolveStatusTag(status, options)?.label ?? status

/**
 * Whether a status is only reachable via a second approval.
 *
 * The filters endpoint lists statuses from the service's enum, so it offers these
 * even to an element with no second approver, where they cannot occur.
 */
export const isSecondStageStatus = status => SUBMITTAL_STATUS_TAGS[status]?.secondStage === true

/**
 * What a message calls the submission itself.
 *
 * "E-Form submission" is the wording EMS carries in production, and an absent
 * mode means eform — the mode was introduced after the eform data elements — so
 * that stays the default and EMS reads exactly as it does today. An element
 * filled in any other way is not an e-form, so it is named plainly instead.
 */
export const submissionNoun = submissionMode =>
  !submissionMode || submissionMode === SUBMISSION_MODE.EFORM ? 'E-Form submission' : 'Submission'

/**
 * The toast shown to the consultant after they act on a submittal.
 *
 * Per mockup, a two-stage element's toast now says "shared to {org}" too, same
 * as single-stage — even though the status tag beside it still reads "Pending
 * {org} Review" until {org} actually acts. The toast is about the consultant's
 * own action having gone through, not a claim that the workflow is finished.
 *
 * Falls back to DEFAULT_SECONDARY_LABEL rather than letting withOrg collapse
 * `{org}` to nothing: unlike the mid-sentence "Pending {org} Review", this
 * placeholder sits right before the full stop, and blanking it would leave the
 * sentence trailing off as "shared to ." rather than a word.
 */
export const approvalToast = ({ submissionMode, receiverOrgName, secondaryApproval } = {}) =>
  secondaryApproval?.required
    ? withOrg(
        'Submission approved and shared to {org}.',
        secondaryApproval.label || DEFAULT_SECONDARY_LABEL
      )
    : `${submissionNoun(submissionMode)} approved and shared to ${receiverOrgName}`

/**
 * A consultant's rejection on a two-stage element still notifies the
 * contractor directly — it does not go through {org}, who never saw the
 * submission — so the wording says who hears about it rather than naming a
 * second stage that was never reached.
 */
export const rejectionToast = ({ submissionMode, secondaryApproval } = {}) =>
  secondaryApproval?.required
    ? 'Submission rejected and the contractor will be notified of your rejection.'
    : `${submissionNoun(submissionMode)} rejected successfully.`
